import { GraphQLFormattedError } from 'graphql';
import { useCallback, useMemo, useState } from 'react';

import {
  ClientUserParentOrGuardianInput,
  ConsentOrigin,
  ConsentType,
  EVERNORTH_CONSENT_TYPES,
  getAgreementLinkItemFromConsentType as getAgreementLinkItemFromConsentTypeBase,
  useCreateExternalEvernorthPatient as useCreateExternalEvernorthPatientBase,
  useSignClientUserConsents as useSignClientUserConsentsBase,
} from '@grow-therapy-team/seedling-components';

import { logger } from '../../../datadog/logger';
import { EventTypeEnum } from '../../../segment/types';
import { normalizeError } from '../../../utils';
import { useTrackIntroRoomEvent as useTrackIntroRoomEventBase } from '../page-content/useTrackIntroRoomEvent';
import { SignConsentsVariables } from './useRequiredAgreements';

type UseSignConsentsProps = {
  consentSigningToken?: string | null;
  onSigningSuccess: () => void;
};

type Dependencies = {
  getAgreementLinkItemFromConsentType: typeof getAgreementLinkItemFromConsentTypeBase;
  logError: typeof logger.error;
  useCreateExternalEvernorthPatient: typeof useCreateExternalEvernorthPatientBase;
  useSignClientUserConsents: typeof useSignClientUserConsentsBase;
  useTrackIntroRoomEvent: typeof useTrackIntroRoomEventBase;
};

/**
 * Convenience hook wrapper around the useSignClientUserConsents mutation hook.
 * Manages mutation context, execution, and error handling.
 *
 * For Evernorth consents, this hook will also create an external Evernorth
 * patient for the client.
 *
 * As we identified that we may hit rate limiting on Evernorth's endpoint,
 * errors from the Evernorth patient creation step are NOT considered critical
 * failures and should not block admission to the waiting room. However, errors
 * from the sign consents step are considered critical failures and should block
 * admission to the waiting room. This is due to downstream legal implications;
 * we are able to recover from the lack of Evernorth patient creation, but only
 * if the consents are on file.
 *
 * @param props - Primary props for the hook:
 * - consentSigningToken: The single-use auth token to use for signing the
 *   required agreements. This is only applicable if the client used PII
 *   verification instead of logging in.
 * - onSigningSuccess: The callback to invoke when the consent signing succeeds,
 *   to proceed to the waiting room. Only failures from the consent signing
 *   block this being called.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 * @returns The signConsents function, the hasError state, and the isSubmitting
 * state.
 */
export function useSignConsents(
  { consentSigningToken, onSigningSuccess }: UseSignConsentsProps,
  {
    getAgreementLinkItemFromConsentType,
    logError,
    useCreateExternalEvernorthPatient,
    useSignClientUserConsents,
    useTrackIntroRoomEvent,
  }: Dependencies = {
    getAgreementLinkItemFromConsentType: getAgreementLinkItemFromConsentTypeBase,
    logError: logger.error,
    useCreateExternalEvernorthPatient: useCreateExternalEvernorthPatientBase,
    useSignClientUserConsents: useSignClientUserConsentsBase,
    useTrackIntroRoomEvent: useTrackIntroRoomEventBase,
  },
) {
  const { formSubmitted } = useTrackIntroRoomEvent();

  const mutationContext = useMemo(
    () =>
      consentSigningToken
        ? {
            // Apply a custom auth header for the consent signing token
            skipAuth: true,
            headers: { Authorization: `ConsentToken ${consentSigningToken}` },
          }
        : undefined,
    [consentSigningToken],
  );

  const [hasError, setHasError] = useState<boolean>(false);

  const [signClientUserConsents, { loading: isSubmitting }] = useSignClientUserConsents({ context: mutationContext });

  const [createExternalEvernorthPatient, { loading: isCreatingExternalEvernorthPatient }] =
    useCreateExternalEvernorthPatient({
      context: { skipAuth: true },
    });

  const executeMutation = useCallback(
    async <T extends { errors?: readonly GraphQLFormattedError[] }>(
      operationName: string,
      operation: () => Promise<T>,
      context: { clientUserShortId: string; flowId: string } & Record<string, unknown>,
      errors: GraphQLFormattedError[],
    ) => {
      try {
        const { errors: operationErrors } = await operation();
        if (operationErrors) {
          errors.push(...operationErrors);
          logError(`Error from consent signing waiting room block for ${operationName}`, {
            ...context,
            errors: operationErrors.map((error) => error.message),
          });
        }
      } catch (error) {
        const normalizedError = normalizeError(error);
        errors.push(normalizedError);
        logError(`Error from consent signing waiting room block for ${operationName}`, {
          ...context,
          errors: [normalizedError.message],
        });
      }
    },
    [logError],
  );

  const signConsents = useCallback(
    async ({
      clientUserShortId,
      consentTypes,
      flowId,
      clientUserParentGuardianShortId,
      clientUserParentOrGuardian,
    }: SignConsentsVariables) => {
      formSubmitted(EventTypeEnum.CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_SIGNED, { flowId });

      const errors: GraphQLFormattedError[] = [];

      await executeMutation(
        'signClientUserConsents',
        () =>
          signClientUserConsents({
            variables: {
              clientUserShortId,
              values: {
                consentOrigin: ConsentOrigin.TELEHEALTH_WAITING_ROOM_BLOCK,
                signedConsents: consentTypes.map((consentType) => {
                  return {
                    consentType,
                    consentFilePath: getAgreementLinkItemFromConsentType(consentType).href,
                  };
                }),
                clientUserParentGuardianShortId,
                clientUserParentOrGuardian:
                  // casting here because relationship field starts as an empty string, but will be prevented from submitting on the frontend when empty
                  clientUserParentOrGuardian as ClientUserParentOrGuardianInput,
              },
            },
          }),
        { clientUserShortId, flowId, consentTypes },
        errors,
      );

      // If there are any errors from the sign consents step, we should not
      // proceed to the Evernorth patient creation step.
      if (errors.length > 0) {
        setHasError(true);
        return;
      }

      if (hasEvernorthConsents(consentTypes)) {
        await executeMutation(
          'createExternalEvernorthPatient',
          () =>
            createExternalEvernorthPatient({
              variables: {
                clientUserShortId,
                createConsentsIfMissing: false,
              },
            }),
          { clientUserShortId, flowId },
          errors,
        );
      }

      onSigningSuccess();
    },
    [
      createExternalEvernorthPatient,
      executeMutation,
      formSubmitted,
      getAgreementLinkItemFromConsentType,
      onSigningSuccess,
      signClientUserConsents,
    ],
  );

  return {
    hasError,
    isSubmitting: isSubmitting || isCreatingExternalEvernorthPatient,
    signConsents,
  };
}

function hasEvernorthConsents(consentTypes: ConsentType[]): boolean {
  return consentTypes.some((consentType) =>
    // Widen the readonly tuple to accept ConsentType as a valid argument
    (EVERNORTH_CONSENT_TYPES as readonly ConsentType[]).includes(consentType),
  );
}
